import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { format, parse, isValid } from "date-fns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { validateOrderOpportunityField } from "./orderdetailsopportunity.helper";

const parseDateValue = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isValid(val) ? val : null;
  const str = String(val).trim();
  if (!str) return null;

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
    const parsed = parse(str, "MM/dd/yyyy", new Date());
    if (isValid(parsed)) return parsed;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const datePart = str.split("T")[0].split(" ")[0];
    const [y, m, d] = datePart.split("-");
    const parsed = new Date(
      Number.parseInt(y, 10),
      Number.parseInt(m, 10) - 1,
      Number.parseInt(d, 10),
    );
    if (isValid(parsed)) return parsed;
  }
  const generalDate = new Date(str);
  if (isValid(generalDate)) return generalDate;

  return null;
};

const OrderDetailsOpportunity = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const [formData, setFormData] = useState(data);

  const [errors, setErrors] = useState({});
  const [touched] = useState({});

  useEffect(() => {
    // Initial validation
    const fields = ["newMedication"];
    const initialErrors = {};
    fields.forEach((field) => {
      const error = validateOrderOpportunityField(field, formData[field]);
      if (error) initialErrors[field] = error;
    });
    setErrors(initialErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateField = (name, value) => {
    const error = validateOrderOpportunityField(name, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      onDataChange(next);
      return next;
    });
    validateField(name, value);
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[name] : "",
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        Order Details (Read Only from Opportunity)
      </Typography>

      <Box sx={{ border: "none", borderRadius: "8px", overflow: "hidden" }}>
        <Table
          size="small"
          sx={{
            "& .MuiTableCell-root": { borderBottom: "none" },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#D8D6E2" }}>
              <TableCell
                sx={{
                  color: "#403C6F",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 1.5,
                  width: "40%",
                }}
              >
                Product Name
              </TableCell>
              <TableCell
                sx={{
                  color: "#403C6F",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 1.5,
                }}
              >
                Strength
              </TableCell>
              <TableCell
                sx={{
                  color: "#403C6F",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 1.5,
                }}
              >
                Qty
              </TableCell>
              <TableCell
                sx={{
                  color: "#403C6F",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 1.5,
                }}
              >
                30 Days
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ backgroundColor: "#F7F8FB" }}>
              <TableCell sx={{ color: "#9F9DB6", fontSize: "14px", py: 2 }}>
                {opportunity?.drug || "Humira Injection"}
              </TableCell>
              <TableCell sx={{ color: "#9F9DB6", fontSize: "14px", py: 2 }}>
                {opportunity?.strength || "500 mg"}
              </TableCell>
              <TableCell sx={{ color: "#9F9DB6", fontSize: "14px", py: 2 }}>
                {opportunity?.metricQty || "8"}
              </TableCell>
              <TableCell sx={{ color: "#9F9DB6", fontSize: "14px", py: 2 }}>
                False
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Box>
        <Typography
          sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500, mb: 0.5 }}
        >
          New Medication?<span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <RadioGroup
          row
          name="newMedication"
          value={formData.newMedication}
          onChange={handleChange}
        >
          <FormControlLabel
            value="Yes"
            labelPlacement="start"
            control={
              <Radio
                size="small"
                sx={{
                  color: "#C6C4D6",
                  "&.Mui-checked": { color: "#403C6F !important" },
                  "& .MuiSvgIcon-root": { color: "inherit" },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#0e1324",
                  fontWeight: 500,
                  mr: 0.5,
                }}
              >
                Yes
              </Typography>
            }
            sx={{ m: 0, mr: 2 }}
          />
          <FormControlLabel
            value="No"
            labelPlacement="start"
            control={
              <Radio
                size="small"
                sx={{
                  color: "#C6C4D6",
                  "&.Mui-checked": { color: "#403C6F !important" },
                  "& .MuiSvgIcon-root": { color: "inherit" },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#0e1324",
                  fontWeight: 500,
                  mr: 0.5,
                }}
              >
                No
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </RadioGroup>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              PA Eff Date
            </Typography>
            <DesktopDatePicker
              value={parseDateValue(formData.paEffDate)}
              onChange={(newValue) => {
                const formattedDate =
                  newValue && isValid(newValue)
                    ? format(newValue, "MM/dd/yyyy")
                    : "";
                setFormData((prev) => {
                  const next = { ...prev, paEffDate: formattedDate };
                  onDataChange(next);
                  return next;
                });
                validateField("paEffDate", formattedDate);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "MM/DD/YYYY",
                  error: getErrorProps("paEffDate").error,
                  helperText: getErrorProps("paEffDate").helperText,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: getErrorProps("paEffDate").error
                          ? "#d32f2f"
                          : "#E8E8E9",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      py: 1.5,
                      color: "#0e1324",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              PA Term Date
            </Typography>
            <DesktopDatePicker
              value={parseDateValue(formData.paTermDate)}
              onChange={(newValue) => {
                const formattedDate =
                  newValue && isValid(newValue)
                    ? format(newValue, "MM/dd/yyyy")
                    : "";
                setFormData((prev) => {
                  const next = { ...prev, paTermDate: formattedDate };
                  onDataChange(next);
                  return next;
                });
                validateField("paTermDate", formattedDate);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "MM/DD/YYYY",
                  error: getErrorProps("paTermDate").error,
                  helperText: getErrorProps("paTermDate").helperText,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: getErrorProps("paTermDate").error
                          ? "#d32f2f"
                          : "#E8E8E9",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      py: 1.5,
                      color: "#0e1324",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

OrderDetailsOpportunity.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default OrderDetailsOpportunity;
